import React, { useState, useEffect } from 'react';
import { Layout, Menu, Avatar, Button, Alert, Typography, Dropdown } from 'antd';
import { 
  HomeOutlined, 
  BarChartOutlined, 
  SettingOutlined, 
  LogoutOutlined,
  ArrowLeftOutlined,
  MenuOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { signOut } from 'firebase/auth';
import { Helmet } from 'react-helmet-async';
import { auth } from '../api/firebase';
import logo from '../assets/images/logo.png';

const { Header, Content, Footer } = Layout;
const { Title, Text } = Typography;

const AppShell = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, loading] = useAuthState(auth);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [selectedKey, setSelectedKey] = useState('1');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        setShowMobileMenu(false); // Close mobile menu on desktop
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle network status (simplified)
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Set selected menu item based on current route
  useEffect(() => {
    const path = location.pathname;
    if (path === '/' || path === '/dashboard') {
      setSelectedKey('1');
    } else if (path === '/analytics') {
      setSelectedKey('2');
    } else if (path === '/settings') {
      setSelectedKey('3');
    } else {
      setSelectedKey('1');
    }
  }, [location]);

  const handleMenuClick = ({ key }) => {
    setShowMobileMenu(false); // Close mobile menu after selection
    switch (key) {
      case '1':
        navigate('/dashboard');
        break;
      case '2':
        navigate('/analytics');
        break;
      case '3':
        navigate('/settings');
        break;
      default:
        break;
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleBackClick = () => {
    navigate('/dashboard');
  };

  // Check if we're on a sub-page that needs back button
  const isSubPage = location.pathname !== '/' && location.pathname !== '/dashboard';
  
  const getPageTitle = () => {
    switch (location.pathname) {
      case '/pomodoro':
        return 'Pomodoro Timer';
      case '/habits':
        return 'Habit Tracker';
      case '/analytics':
        return 'Analytics';
      case '/settings':
        return 'Settings';
      default:
        return 'Progress Craft';
    }
  };

  const getPageMetadata = () => {
    const baseUrl = 'https://tools.progresscraft.com';
    
    switch (location.pathname) {
      case '/pomodoro':
        return {
          title: '🍅 Pomodoro Timer - Progress Craft',
          description: 'Stay focused with our Pomodoro Timer. Track work sessions, breaks, and boost your productivity with proven time management techniques.',
          keywords: 'pomodoro timer, focus timer, productivity timer, work sessions, time management, focus technique',
          url: `${baseUrl}/pomodoro`,
          image: `${baseUrl}/pomodoro-preview.png`
        };
      case '/habits':
        return {
          title: '✅ Habit Tracker - Progress Craft',
          description: 'Build lasting habits with our Habit Tracker. Monitor daily habits, track streaks, and develop positive routines for personal growth.',
          keywords: 'habit tracker, daily habits, habit building, streak tracker, personal development, routine tracking',
          url: `${baseUrl}/habits`,
          image: `${baseUrl}/habits-preview.png`
        };
      case '/analytics':
        return {
          title: '📊 Analytics Dashboard - Progress Craft',
          description: 'Analyze your productivity patterns with detailed analytics. View focus session statistics, habit completion rates, and progress insights.',
          keywords: 'productivity analytics, habit analytics, focus session stats, productivity insights, progress tracking',
          url: `${baseUrl}/analytics`,
          image: `${baseUrl}/analytics-preview.png`
        };
      case '/settings':
        return {
          title: '⚙️ Settings - Progress Craft',
          description: 'Customize your Progress Craft experience. Adjust timer settings, notification preferences, and manage your productivity tools.',
          keywords: 'settings, preferences, customization, timer settings, notifications, productivity settings',
          url: `${baseUrl}/settings`,
          image: `${baseUrl}/settings-preview.png`
        };
      default:
        return {
          title: '⚡ Progress Craft - Productivity Hub | Pomodoro Timer & Habit Tracker',
          description: 'Boost your productivity with Progress Craft. Features Pomodoro Timer, Habit Tracker, and detailed analytics. Perfect for students, mentors, and productivity enthusiasts.',
          keywords: 'pomodoro timer, habit tracker, productivity app, focus sessions, habit building, productivity analytics, student tools, time management',
          url: baseUrl,
          image: `${baseUrl}/social-share.png`
        };
    }
  };

  // Simplified user menu - removed profile option
  const userMenuItems = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Sign Out',
      onClick: handleSignOut
    }
  ];

  const menuItems = [
    {
      key: '1',
      icon: <HomeOutlined />,
      label: 'Home',
    },
    {
      key: '2',
      icon: <BarChartOutlined />,
      label: 'Analytics',
    },
    {
      key: '3',
      icon: <SettingOutlined />,
      label: 'Settings',
    },
  ];

  const pageMetadata = getPageMetadata();

  if (loading) {
    return (
      <Layout style={{ minHeight: '100vh' }}>
        <Content style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div>Loading...</div>
        </Content>
      </Layout>
    );
  }

  return (
    <>
      <Helmet>
        <title>{pageMetadata.title}</title>
        <meta name="description" content={pageMetadata.description} />
        <meta name="keywords" content={pageMetadata.keywords} />
        <link rel="canonical" href={pageMetadata.url} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:url" content={pageMetadata.url} />
        <meta property="og:title" content={pageMetadata.title} />
        <meta property="og:description" content={pageMetadata.description} />
        <meta property="og:image" content={pageMetadata.image} />
        
        {/* Twitter Card */}
        <meta name="twitter:url" content={pageMetadata.url} />
        <meta name="twitter:title" content={pageMetadata.title} />
        <meta name="twitter:description" content={pageMetadata.description} />
        <meta name="twitter:image" content={pageMetadata.image} />
      </Helmet>

      <Layout style={{ minHeight: '100vh' }}>
        <Header style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          padding: '0 16px',
          background: '#1E90FF',
          height: '64px',
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          {/* Left side - Logo/Back button + Title */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center',
            flex: 1,
            minWidth: 0, // Allows flex item to shrink
            maxWidth: isMobile ? 'calc(100% - 120px)' : 'none' // Prevent overflow on mobile
          }}>
            {isSubPage ? (
              // Back button for sub-pages (mobile-first approach)
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={handleBackClick}
                style={{
                  color: 'white',
                  fontSize: '18px',
                  padding: '0 8px',
                  marginRight: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  minWidth: '40px'
                }}
              />
            ) : (
              // Logo for home page
              <img 
                src={logo} 
                alt="Progress Craft Logo" 
                style={{ 
                  height: '32px',
                  width: 'auto',
                  marginRight: '12px',
                  flexShrink: 0
                }} 
              />
            )}
            
            <Title 
              level={4} 
              style={{ 
                margin: 0, 
                color: 'white',
                fontWeight: 'bold',
                fontSize: isMobile ? '16px' : '18px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {getPageTitle()}
            </Title>
          </div>

          {/* Right side - Navigation & User */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
            {/* Desktop Navigation Menu */}
            {!isMobile && (
              <Menu
                theme="dark"
                mode="horizontal"
                selectedKeys={[selectedKey]}
                items={menuItems}
                onClick={handleMenuClick}
                style={{ 
                  background: 'transparent',
                  border: 'none',
                  minWidth: '180px'
                }}
              />
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="hamburger-button"
                style={{
                  color: 'white',
                  fontSize: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '40px',
                  height: '40px'
                }}
              />
            )}

            {/* User Menu */}
            {user ? (
              <Dropdown
                menu={{ items: userMenuItems }}
                placement="bottomRight"
                trigger={['click']}
              >
                <Button 
                  type="text" 
                  style={{ 
                    height: 'auto', 
                    padding: '4px 8px',
                    color: 'white',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <Avatar 
                    size="small" 
                    src={user.photoURL} 
                    icon={<LogoutOutlined />}
                    style={{ backgroundColor: '#87CEEB' }}
                  />
                  {!isMobile && (
                    <span style={{ 
                      color: 'white',
                      marginLeft: '8px'
                    }}>
                      {user.displayName?.split(' ')[0] || user.email?.split('@')[0]}
                    </span>
                  )}
                </Button>
              </Dropdown>
            ) : (
              <Button 
                type="primary" 
                style={{ 
                  backgroundColor: '#FFA500',
                  borderColor: '#FFA500',
                  fontSize: '14px'
                }}
                onClick={() => navigate('/login')}
              >
                Sign In
              </Button>
            )}
          </div>
        </Header>

        {/* Mobile Navigation Dropdown */}
        {showMobileMenu && isMobile && (
          <div className="mobile-menu-overlay">
            <Menu
              theme="dark"
              mode="vertical"
              selectedKeys={[selectedKey]}
              items={menuItems}
              onClick={handleMenuClick}
              className="mobile-nav-menu"
            />
          </div>
        )}

        <Content style={{ 
          padding: isMobile ? '16px' : '24px',
          background: '#F0F2F5',
          minHeight: 'calc(100vh - 64px - 70px)',
          paddingTop: showMobileMenu && isMobile ? '16px' : (isMobile ? '16px' : '24px')
        }}>
          {/* Simplified offline alert - only show if offline */}
          {!isOnline && (
            <Alert
              message="Offline Mode"
              description="Changes will sync when you're back online."
              type="warning"
              style={{ marginBottom: '16px' }}
              closable
              showIcon={false}
            />
          )}

          {children}
        </Content>

        <Footer style={{ 
          textAlign: 'center',
          background: '#F0F2F5',
          color: '#4B4B4B',
          padding: isMobile ? '16px' : '24px',
          borderTop: '1px solid #d9d9d9'
        }}>
          <Text type="secondary" style={{ fontSize: isMobile ? '11px' : '12px' }}>
            © 2024 Progress Craft - Empowering Productivity & Growth
          </Text>
        </Footer>
      </Layout>
    </>
  );
};

export default AppShell; 